<template>
  <app-modal :modal-id="modalId"
             :modal-title="modalTitle"
             ok-button-title="Renew"
             @confirm="dateSelected"
             @beforeShow="onBeforeShow"
             @afterHidden="onHidden"
             @cancel="cancel">
    <template>
      <div class="row">
        <div class="col-12" v-if="isBatchRenewal()">
          Set an expiration date for the following groups:
          <div class="row row-indent scroll-container">
            <div v-for="(groupId) in groupIds" :key="groupId" class="col-12">
              {{groupId}}
            </div>
          </div>
        </div>
        <div class="col-12">
          <b-form-group :label="radioGroupLabel">
            <b-form-radio-group id="renew-group-options"
                                v-model="renewMethod"
                                name="renew-group-options"
                                stacked>
              <b-form-radio id="rdo-max-renew" value="max-renew">
                The maximum expiration date: <strong><em>{{ formattedMaxExpirationString() }}</em></strong>
              </b-form-radio>
              <b-form-radio id="rdo-choose-renew" value="choose-renew">
                Let me choose
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </div>
        <b-collapse :visible="renewMethod === 'choose-renew'" id="date-picker-collapse-container">
            <div class="col-12">
              <b-form-group id="fg-group-renew-date_picker"
                            :label-sr-only="true"
                            :description="manualSelectDescription">
                <date-picker id="group-renew-date_picker"
                             v-model="datePickerSelectedDate"
                             :disabled-dates="disabledDates()"
                             :format="dateFormatter"
                             placeholder="Choose a date"
                             :bootstrap-styling="true"
                             :typeable="true"
                             @opened="selectedExpirationDate = ''"
                             @selected="validateAndSetManualDate"
                             @cleared="isValid=true">
                </date-picker>
                <span v-if="!isValid" class="inline-error-message">Date is invalid</span>
              </b-form-group>
          </div>
        </b-collapse>
      </div>
    </template>
  </app-modal>
</template>

<script>
/* NOTE: See choose-expiration-mixin.js for most of the common Expiration attributes/options */
import * as moment from 'moment';
import expiration from '../../util/choose-expiration-mixin';

export default {
  name: 'GroupExpirationModal',
  mixins: [expiration],
  props: {
    modalId: {
      type: String,
      required: true,
    },
    groupIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      /* The callback event that will be emitted when 'Confirm' is selected. */
      emittedEvent: 'groupExpirationSelected',
      renewMethod: '',
      manualSelectDescription: `Min expire date: ${this.formattedMinExpirationString()}<br/>` +
      `Max expire date: ${this.formattedMaxExpirationString()}`,
      datePickerSelectedDate: '',
    };
  },
  computed: {
    modalTitle() {
      if (this.isSingleGroupRenewal()) {
        return `Choose Expiration Date for ${this.groupIds[0]}`;
      } else if (this.isBatchRenewal()) {
        return 'Choose Expiration Date';
      }
      return 'You must choose at least 1 group to be renewed.';
    },
    radioGroupLabel() {
      if (this.isSingleGroupRenewal()) {
        return `This group is set to expire on ${this.formattedCurExpiration()}. To renew, please choose another date.`;
      }
      return 'Choose expiration date:';
    },
  },
  methods: {
    isSingleGroupRenewal() {
      return this.groupIds.length === 1;
    },
    isBatchRenewal() {
      return this.groupIds.length > 1;
    },
    /* Called when the Confirm/Renew button is clicked and the selected date is valid. */
    emitConfirmedEvent(selectedDate) {
      this.$emit(this.emittedEvent, selectedDate, this.groupIds);
      this.resetModalState();
    },
    /* Max renewal date for a Groups is 14 months from today */
    maxExpireDate() {
      return moment().add(14, 'months').startOf('day').toDate();
    },
    /* Dates that will be disabled in the datePicker component */
    disabledDates() {
      return {
        // Disable dates up to today, inclusively
        to: moment().toDate(),
        // Disable all dates after specific date
        from: this.maxExpireDate(),
      };
    },
    isValidRenewalDate(date) {
      return moment(date).isBetween(this.minExpireDate(), this.maxExpireDate(), null, '[]');
    },
    formattedCurExpiration() {
      const group = this.$store.getters.administeredGroups
        .find(g => g.key === this.groupIds[0]);
      if (group) {
        return moment(group.managedGroup.expirationDate).format('dddd, MMMM Do YYYY').toString();
      }
      return 'Unset';
    },
    /* Event that is triggered immediately before the modal is shown */
    onBeforeShow() {
      this.setInitialState();
    },
    /* Event that is triggered immediately after the modal closes */
    onHidden() {
      this.resetModalState();
    },
    resetModalState() {
      this.setInitialState();
    },
    setInitialState() {
      this.renewMethod = 'max-renew';
      this.selectedExpirationDate = this.maxExpireDate();
      this.datePickerSelectedDate = '';
    },
    validateAndSetManualDate(date) {
      this.validateSelectedDate(date);
      if (this.isValid) {
        this.selectedExpirationDate = date;
      } else {
        this.selectedExpirationDate = '';
      }
    },
  },
  watch: {
    /* Update the selectedExpirationDate based on which radio button was JUST selected */
    renewMethod() {
      if (this.renewMethod === 'max-renew') {
        this.datePickerSelectedDate = '';
        this.selectedExpirationDate = this.maxExpireDate();
      } else if (this.renewMethod === 'choose-renew') {
        this.selectedExpirationDate = '';
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/modals/GroupRenewModal.vue